<template>
  <span v-if="(paymentMethods?.length ?? 0) > 0" class="font-bold leading-6">{{
    $t("shared.payment.bank_card_form.select_saved_card")
  }}</span>
  <div class="md:px-7">
    <div class="selected-address flex flex-col gap-4 md:-mx-7">
      <div
        v-for="(card, index) in paymentMethods"
        :key="index"
        class="selected-address__item test flex flex-col items-start justify-between"
      >
        <div class="flex w-full items-start justify-between">
          <VcRadioButton
            :id="`cardBox_${index}`"
            v-model="selectedCardId"
            name="userCards"
            :value="card.id"
            class="my-4 items-start last-of-type:mb-0"
          >
            <ul class="flex max-w-[250px] flex-col font-medium leading-6">
              <li class="font-bold">
                {{ formatCreditCardType(card.cardDetails.cardType) }} Ending in
                {{ card.cardDetails.maskedCardNumber.slice(-4) }}
              </li>
              <li class="font-medium">
                {{ $t("common.labels.expiration") }}: {{ card.cardDetails.expirationMonth }}/{{
                  card.cardDetails.expirationYear.slice(-2)
                }}
              </li>
              <li>
                <span class="font-medium capitalize">
                  {{ card.cardDetails.cardholderFirstName.toLocaleLowerCase() }}
                  {{ card.cardDetails.cardholderLastName.toLocaleLowerCase() }}
                </span>
              </li>
            </ul>
          </VcRadioButton>
          <VcButton
            type="button"
            variant="link"
            size="lg"
            class="my-4 text-[--link-color] underline"
            @click="showDialogPaymentModal(card.cardDetails)"
          >
            {{ $t("common.buttons.edit") }}
          </VcButton>
        </div>
        <div
          v-if="!isCardValid(card.cardDetails, currentCurrency.code, cardTypesMap) && selectedCardId === card.id"
          class="mb-4 mt-1 flex flex-row items-start gap-3"
        >
          <VcIcon name="information-circle" :size="18" class="error-icon-semibold rotate-180"></VcIcon>
          <span class="error-text">
            {{ getCardUnavailableMessage(card.cardDetails, currentCurrency.code, cardTypesMap, t) }}
          </span>
        </div>
      </div>
      <div v-if="(paymentMethods?.length ?? 0) > 0" class="flex items-center justify-between">
        <VcButton color="primary" size="lg" variant="link" @click="showDialogPaymentModal()">
          <VcIcon name="plus-alt" class="mr-2" :size="16"></VcIcon>
          <span class="inline font-semibold capitalize">{{ $t("common.buttons.add_payment_card") }}</span>
        </VcButton>
      </div>
    </div>
  </div>

  <AddOrUpdatePaymentModal v-if="(paymentMethods?.length ?? 0) > 0" ref="paymentModalRefs"></AddOrUpdatePaymentModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { createAuthenticationSetup } from "@/core/api/graphql";
import { useCurrency, useDataAreaId, useFooterBlock } from "@/core/composables";
import { Logger } from "@/core/utilities";
import { useUser, useUserPayments } from "@/shared/account";
import { isCardValid, getCardUnavailableMessage } from "@/shared/account/utils/paymentUtils";
import { useDeviceDataCollection } from "@/shared/payment/composables/useDeviceDataCollection";
import { usePayerAuthentication } from "@/shared/payment/composables/usePayerAuthentication";
import { cardTypesMap } from "@/shared/payment/constants";
import { eventBus } from "@/shared/thorlabs/shared";
import type { PaymentInstrumentDetailsType } from "@/core/api/graphql/types";
import AddOrUpdatePaymentModal from "@/shared/account/components/add-or-update-payment-modal.vue";

interface IEmits {
  (event: "savedCreditCardsLoaded"): void;
}

const emit = defineEmits<IEmits>();

const { t } = useI18n();
const { user } = useUser();
const { fetchPayments, payments } = useUserPayments();
const { billingDataAreaId, getDataAreaId } = useDataAreaId();
const { currentCurrency } = useCurrency();
const { performDeviceDataCollection } = useDeviceDataCollection();
const { is3DSPayerAuthenticationEnabled } = usePayerAuthentication();

const paymentModalRefs = ref<InstanceType<typeof AddOrUpdatePaymentModal> | null>(null);
const referenceId = ref<string>("");
const showDialogPaymentModal = (card?: PaymentInstrumentDetailsType) => {
  paymentModalRefs.value?.openDialog(card);
};
const paymentMethods = computed(() => {
  return payments.value?.map((item, index) => {
    return {
      id: index.toString(),
      cardDetails: item,
    };
  });
});
const selectedCardId = ref<string>("");

const selectedCard = computed(() => {
  return paymentMethods.value?.find((item) => item.id === selectedCardId.value);
});

const isSelectedCardValid = computed(() => {
  return selectedCard.value
    ? isCardValid(selectedCard.value.cardDetails, currentCurrency.value.code, cardTypesMap)
    : false;
});

const formatCreditCardType = (cardType: string) => {
  return cardType.charAt(0).toUpperCase() + cardType.slice(1).toLowerCase();
};

const getParameters = (dataAreaId?: string) => {
  if (selectedCard.value) {
    return [
      {
        key: "expirationDate",
        value: `${selectedCard.value?.cardDetails?.expirationMonth}-20${selectedCard.value?.cardDetails?.expirationYear.slice(-2)}`,
      },
      {
        key: "cardholderName",
        value: `${selectedCard.value?.cardDetails?.cardholderFirstName} ${selectedCard.value?.cardDetails?.cardholderLastName}`,
      },
      {
        key: "maskedCardNumber",
        value: selectedCard.value?.cardDetails?.maskedCardNumber,
      },
      {
        key: "cardType",
        value: selectedCard.value?.cardDetails?.cardType,
      },
      {
        key: "paymentInstrumentTokenId",
        value: selectedCard.value?.cardDetails?.paymentInstrumentTokenId,
      },
      {
        key: "dataAreaId",
        value: dataAreaId ?? billingDataAreaId.value,
      },
      {
        key: "referenceId",
        value: referenceId.value,
      },
    ];
  }
};

const selectDefaultCard = () => {
  if (paymentMethods.value && paymentMethods.value.length > 0) {
    // Find the default card
    const defaultCardIndex = payments.value?.findIndex((card) => card.isDefault) ?? 0;
    selectedCardId.value = paymentMethods.value[defaultCardIndex]?.id ?? paymentMethods.value[0].id;
  }
};

onMounted(async () => {
  try {
    await fetchPayments();
    selectDefaultCard();
    emit("savedCreditCardsLoaded");
  } catch (error) {
    Logger.error("Failed loading saved credit cards: ", error);
    const footerBlockComposable = useFooterBlock();
    const htmlPhone = `<a href="tel:${footerBlockComposable?.footerBlock.value?.salesPhone ?? ""}" class='text-[--color-link]'>${footerBlockComposable?.footerBlock.value?.salesPhone ?? ""}</a>`;

    eventBus.emit("showCreditCardFormError", {
      message: t("shared.payment.bank_card_form.payment_processor_unavailable_with_phone_number", [htmlPhone]),
    });
  }
});

watch(selectedCardId, async (newCardId) => {
  referenceId.value = "";
  const card = paymentMethods.value?.find((item) => item.id === newCardId);
  if (card) {
    const dataAreaId =
      getDataAreaId(currentCurrency.value.code, card.cardDetails.billingAddress?.countryCode ?? "") ??
      billingDataAreaId.value;
    if (!is3DSPayerAuthenticationEnabled(dataAreaId)) {
      return;
    }
    try {
      const setupResult = await createAuthenticationSetup(
        "",
        dataAreaId,
        card.cardDetails.paymentInstrumentTokenId,
        user.value?.id ?? "",
      );
      await performDeviceDataCollection(setupResult);

      if (setupResult.referenceId) {
        referenceId.value = setupResult.referenceId;
      }
    } catch (e) {
      Logger.error("createAuthenticationSetup", e);
    }
  }
});

defineExpose({
  selectedCard: computed(() => selectedCard.value),
  isSelectedCardValid: computed(() => isSelectedCardValid.value),
  getParameters,
});
</script>

<style lang="scss" scoped>
:deep(.vc-radio-button__input) {
  @apply mt-1;
}
</style>
